import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Paper, Alert, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import indexedDBService from '../../services/IndexedDBService';

interface IndexedDBLoadingIndicatorProps {
  onInitialized?: () => void;
}

/**
 * Component to display IndexedDB initialization progress
 */
const IndexedDBLoadingIndicator: React.FC<IndexedDBLoadingIndicatorProps> = ({ onInitialized }) => {
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(indexedDBService.isInitialized());
  const [stage, setStage] = useState<string>('Initializing database...');
  
  useEffect(() => {
    // Check initialization status periodically
    const checkProgress = () => {
      const currentProgress = indexedDBService.initProgress;
      const currentError = indexedDBService.initError;
      
      setProgress(currentProgress);
      setError(currentError);
      
      // Update stage message based on progress
      if (currentProgress < 30) {
        setStage('Opening database connection...');
      } else if (currentProgress < 60) {
        setStage('Creating object stores...');
      } else if (currentProgress < 80) {
        setStage('Setting up indexes...');
      } else if (currentProgress < 95) {
        setStage('Preparing initial cache...');
      } else {
        setStage('Almost ready...');
      }
      
      // If already initialized, stop checking
      if (indexedDBService.isInitialized()) {
        setStage('Database initialization complete!');
        setIsInitialized(true);
        if (onInitialized) {
          setTimeout(() => {
            onInitialized();
          }, 500); // Slight delay to show the complete state
        }
        return;
      }
      
      // If not initialized yet, check again in 100ms
      setTimeout(checkProgress, 100);
    };
    
    // Start checking
    checkProgress();
  }, [onInitialized]);
  
  // If already initialized, don't show anything
  if (isInitialized) {
    return null;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ duration: 0.3 }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          maxWidth: 450, 
          mx: 'auto',
          mt: 2,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Box display="flex" alignItems="center" mb={1}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{ marginRight: 12 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z"
                opacity=".3"
              />
              <path
                fill="currentColor"
                d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"
              />
            </svg>
          </motion.div>
          <Typography variant="h6" fontWeight="600">
            Offline Database Setup
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Typography variant="body1" fontWeight="medium" color="text.primary" mb={1}>
          {stage}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" mb={2}>
          Please wait while we prepare your offline database for a faster experience. 
          This only takes a few moments.
        </Typography>
        
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            my: 2, 
            height: 8, 
            borderRadius: 4,
            background: 'rgba(0,0,0,0.05)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, #4776E6 0%, #8E54E9 100%)'
            }
          }}
        />
        
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            {progress < 100 ? 'Preparing offline capabilities' : 'Ready to work offline'}
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="primary">
            {Math.round(progress)}%
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </motion.div>
  );
};

export default IndexedDBLoadingIndicator; 